import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, Eye, Lightbulb, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  EMPTY_HALF_ATTEMPT,
  HALF_EQUATION_CASES,
  MAX_HELPER_COUNT,
  MAX_MAIN_COEFFICIENT,
  SPECIES,
  attemptToEquation,
  checkHalfEquationAttempt,
  formatCharge,
  halfEquationText,
  type EquationSide,
  type HalfEquationAttempt,
  type HalfEquationCheckResult,
  type HelperChoice,
  type RedoxProcess,
} from './electrochemicalCells';

const PROCESS_OPTIONS: { value: RedoxProcess; label: string; hint: string }[] = [
  { value: 'oxidation', label: 'Oxidation', hint: 'Loss of electrons' },
  { value: 'reduction', label: 'Reduction', hint: 'Gain of electrons' },
];

const ACIDIC_STEPS = [
  'Balance the element that changes oxidation state.',
  'Balance oxygen by adding H₂O.',
  'Balance hydrogen by adding H⁺.',
  'Balance charge by adding electrons to the more positive side.',
];

function parseCount(value: string): number {
  return value.trim() === '' ? 0 : Number(value);
}

function CountField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs font-extrabold uppercase tracking-wide text-slate2">
      {label}
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        value={Number.isFinite(value) ? value : ''}
        onChange={(event) => onChange(parseCount(event.target.value))}
        className="w-full rounded-xl border border-hairline bg-white px-3 py-2 text-base font-extrabold normal-case tracking-normal text-ink outline-none focus:border-brand-sky"
      />
    </label>
  );
}

function HelperRow({
  name,
  symbol,
  choice,
  onChange,
}: {
  name: string;
  symbol: string;
  choice: HelperChoice;
  onChange: (choice: HelperChoice) => void;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-end gap-3 rounded-2xl border border-hairline bg-surface p-3">
      <CountField
        label={`${name} count`}
        value={choice.count}
        min={0}
        max={MAX_HELPER_COUNT}
        onChange={(count) => onChange({ ...choice, count })}
      />
      <label className="flex min-w-0 flex-col gap-1 text-xs font-extrabold uppercase tracking-wide text-slate2">
        {`${name} side`}
        <select
          value={choice.side}
          onChange={(event) => onChange({ ...choice, side: event.target.value as EquationSide })}
          className="w-full rounded-xl border border-hairline bg-white px-3 py-2 text-sm font-extrabold normal-case tracking-normal text-ink"
        >
          <option value="left">Left ({symbol} added to reactants)</option>
          <option value="right">Right ({symbol} added to products)</option>
        </select>
      </label>
    </div>
  );
}

function TallyTable({ result }: { result: Extract<HalfEquationCheckResult, { status: 'checked' }> }) {
  return (
    <div className="overflow-x-auto">
      <table className="mt-3 w-full min-w-[16rem] text-left text-sm" aria-label="Atom and charge tally">
        <thead>
          <tr className="text-xs font-extrabold uppercase tracking-wide text-slate2">
            <th className="py-1 pr-3">Count</th>
            <th className="py-1 pr-3">Left</th>
            <th className="py-1 pr-3">Right</th>
            <th className="py-1">Balanced?</th>
          </tr>
        </thead>
        <tbody className="font-bold text-ink">
          {result.report.atoms.map((row) => (
            <tr key={row.element} className="border-t border-hairline">
              <td className="py-1 pr-3">{row.element} atoms</td>
              <td className="py-1 pr-3">{row.left}</td>
              <td className="py-1 pr-3">{row.right}</td>
              <td className="py-1">{row.left === row.right ? 'Yes' : 'No'}</td>
            </tr>
          ))}
          <tr className="border-t border-hairline">
            <td className="py-1 pr-3">Total charge</td>
            <td className="py-1 pr-3">{formatCharge(result.report.chargeLeft)}</td>
            <td className="py-1 pr-3">{formatCharge(result.report.chargeRight)}</td>
            <td className="py-1">{result.report.chargeBalanced ? 'Yes' : 'No'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function HalfEquationMode() {
  const [caseId, setCaseId] = useState(HALF_EQUATION_CASES[0].id);
  const [attempt, setAttempt] = useState<HalfEquationAttempt>({ ...EMPTY_HALF_ATTEMPT });
  const [result, setResult] = useState<HalfEquationCheckResult | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  useScienceTutorSection('HalfEquationMode', { caseId, attempt, showAnswer });
  const item = HALF_EQUATION_CASES.find((entry) => entry.id === caseId) ?? HALF_EQUATION_CASES[0];
  const preview = useMemo(() => halfEquationText(attemptToEquation(item, attempt)), [item, attempt]);

  const update = (next: Partial<HalfEquationAttempt>) => {
    setAttempt((current) => ({ ...current, ...next }));
    setResult(null);
  };

  const chooseCase = (id: string) => {
    setCaseId(id);
    setAttempt({ ...EMPTY_HALF_ATTEMPT });
    setResult(null);
    setShowAnswer(false);
  };

  const checked = result?.status === 'checked' ? result : null;
  const fullyCorrect = checked ? checked.equationCorrect && checked.processCorrect : false;

  return (
    <section className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          Choose a change
        </p>
        <div className="mt-4 grid gap-2" aria-label="Half-equation cases">
          {HALF_EQUATION_CASES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={entry.id === caseId}
              onClick={() => chooseCase(entry.id)}
              className={`rounded-2xl border px-4 py-3 text-left ${entry.id === caseId ? 'border-brand-sky bg-wash-sky' : 'border-hairline bg-surface hover:bg-wash-sky'}`}
            >
              <span className="block text-sm font-extrabold text-ink">{entry.title}</span>
              <span className="mt-1 block text-xs font-bold text-slate2">
                {SPECIES[entry.main.left].display} → {SPECIES[entry.main.right].display}
                {entry.medium === 'acidic' ? ' · acidic' : ''}
              </span>
            </button>
          ))}
        </div>
      </aside>

      <div className="space-y-6">
        <section className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
            {item.medium === 'acidic' ? 'Acidic solution' : 'Simple ion or element change'}
          </p>
          <h2 className="mt-2 text-2xl font-extrabold text-ink">{item.title}</h2>
          <p className="mt-2 text-sm font-medium leading-6 text-slate2">{item.context}</p>
          <p
            className="mt-4 rounded-2xl border border-brand-sky/25 bg-wash-sky px-4 py-3 text-lg font-extrabold text-ink"
            aria-label="Unbalanced skeleton"
          >
            {SPECIES[item.main.left].display} → {SPECIES[item.main.right].display}
          </p>
          {item.medium === 'acidic' ? (
            <p className="mt-2 text-xs font-bold leading-5 text-slate2">
              In acidic solution you may add H₂O and H⁺ to balance oxygen and hydrogen.
            </p>
          ) : (
            <p className="mt-2 text-xs font-bold leading-5 text-slate2">
              No oxygen or hydrogen to balance: use coefficients and electrons only.
            </p>
          )}

          <fieldset className="mt-6">
            <legend className="text-sm font-extrabold text-ink">
              1. Predict: is this oxidation or reduction?
            </legend>
            <div className="mt-3 flex flex-wrap gap-2">
              {PROCESS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={attempt.process === option.value}
                  onClick={() => update({ process: option.value })}
                  className={`rounded-full border px-5 py-2 text-sm font-extrabold ${attempt.process === option.value ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'}`}
                >
                  {option.label}
                  <span className="sr-only"> — {option.hint}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="mt-6">
            <legend className="text-sm font-extrabold text-ink">2. Build the half-equation</legend>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <CountField
                label={`Coefficient of ${SPECIES[item.main.left].display}`}
                value={attempt.leftCoefficient}
                min={1}
                max={MAX_MAIN_COEFFICIENT}
                onChange={(leftCoefficient) => update({ leftCoefficient })}
              />
              <CountField
                label={`Coefficient of ${SPECIES[item.main.right].display}`}
                value={attempt.rightCoefficient}
                min={1}
                max={MAX_MAIN_COEFFICIENT}
                onChange={(rightCoefficient) => update({ rightCoefficient })}
              />
            </div>
            <div className="mt-3 grid gap-3">
              {item.medium === 'acidic' ? (
                <>
                  <HelperRow
                    name="Water"
                    symbol="H₂O"
                    choice={attempt.water}
                    onChange={(water) => update({ water })}
                  />
                  <HelperRow
                    name="Hydrogen ion"
                    symbol="H⁺"
                    choice={attempt.hydrogen}
                    onChange={(hydrogen) => update({ hydrogen })}
                  />
                </>
              ) : null}
              <HelperRow
                name="Electron"
                symbol="e⁻"
                choice={attempt.electrons}
                onChange={(electrons) => update({ electrons })}
              />
            </div>
          </fieldset>

          <p className="mt-5 text-xs font-extrabold uppercase tracking-wide text-slate2">
            Your equation
          </p>
          <p
            className="mt-1 break-words rounded-2xl bg-surface px-4 py-3 text-base font-extrabold text-ink"
            aria-label="Your half-equation"
          >
            {preview}
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!attempt.process}
              onClick={() => setResult(checkHalfEquationAttempt(item, attempt))}
              className="btn-pill-primary disabled:cursor-not-allowed disabled:opacity-40"
            >
              Check half-equation
            </button>
            <button
              type="button"
              onClick={() => setShowAnswer((value) => !value)}
              className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink"
            >
              <Eye className="h-4 w-4" aria-hidden="true" />
              {showAnswer ? 'Hide balanced equation' : 'Show balanced equation'}
            </button>
            <button
              type="button"
              onClick={() => chooseCase(item.id)}
              className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-slate2"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
            </button>
          </div>

          {result?.status === 'invalid' ? (
            <p
              role="status"
              className="mt-5 rounded-2xl border border-brand-coral/30 bg-wash-coral p-4 text-sm font-bold text-ink"
            >
              {result.reason}
            </p>
          ) : null}

          {checked ? (
            <div
              role="status"
              className={`mt-5 rounded-[1.75rem] border p-5 ${fullyCorrect ? 'border-brand-mint/35 bg-wash-mint' : 'border-brand-coral/30 bg-wash-coral'}`}
            >
              <div className="flex items-start gap-3">
                {fullyCorrect ? (
                  <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-mint" aria-hidden="true" />
                ) : (
                  <Lightbulb className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
                )}
                <div className="min-w-0">
                  <h3 className="font-extrabold text-ink">
                    {fullyCorrect
                      ? 'Balanced and correctly classified.'
                      : checked.equationCorrect
                        ? 'Balanced, but check the classification.'
                        : 'Not balanced yet.'}
                  </h3>
                  <p className="mt-1 text-sm font-medium leading-6 text-slate2">
                    {checked.processMessage}
                  </p>
                  <p className="mt-1 text-sm font-medium leading-6 text-slate2">
                    {checked.issue.message}
                  </p>
                </div>
              </div>
              <TallyTable result={checked} />
            </div>
          ) : null}

          {showAnswer ? (
            <div className="mt-5 rounded-2xl border border-brand-sunshine/40 bg-wash-sunshine p-4">
              <p className="text-xs font-extrabold uppercase tracking-wide text-ink-soft">
                Balanced {item.process} half-equation
              </p>
              <p className="mt-1 break-words text-base font-extrabold text-ink">
                {halfEquationText(item.reference)}
              </p>
              {item.medium === 'acidic' ? (
                <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm font-medium text-slate2">
                  {ACIDIC_STEPS.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="rounded-3xl border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
          <strong className="text-ink">Remember:</strong> oxidation is loss of electrons (electrons
          on the right), reduction is gain of electrons (electrons on the left). A balanced
          half-equation has the same number of each atom and the same total charge on both sides.
        </section>
      </div>
    </section>
  );
}
